/**
 * Broker registration wizard configuration.
 * Central config for auth modes, labels, validation, and reference content per broker.
 */

export type AuthMode = 'oauth' | 'totp';

export interface BrokerRegistrationConfig {
  authModes: AuthMode[];
  requiresApiCredentials: boolean;
  accountIdLabel: string;
  accountIdPlaceholder: string;
  accountIdHelperText: string;
  accountIdMinLength: number;
  step2NotRequiredCopy: string;
  apiKeyLabel?: string;
  apiSecretLabel?: string;
  apiKeyHelperText?: string;
  apiSecretHelperText?: string;
}

const DEFAULT_ACCOUNT_ID_MIN_LENGTH = 2;

export const BROKER_REGISTRATION_CONFIG: Record<string, BrokerRegistrationConfig> = {
  zerodha: {
    authModes: ['oauth', 'totp'],
    requiresApiCredentials: true,
    accountIdLabel: 'Zerodha Client ID',
    accountIdPlaceholder: 'e.g., DN1234',
    accountIdHelperText: 'Your Zerodha Client ID',
    accountIdMinLength: DEFAULT_ACCOUNT_ID_MIN_LENGTH,
    step2NotRequiredCopy: 'No API keys required for Zerodha.',
    apiKeyLabel: 'Kite Connect API Key',
    apiSecretLabel: 'Kite Connect API Secret',
    apiKeyHelperText: 'Create an app at kite.zerodha.com and paste the API key',
    apiSecretHelperText: 'API secret from the same app (keep it private)',
  },
  upstox: {
    authModes: ['oauth'],
    requiresApiCredentials: true,
    accountIdLabel: 'User ID',
    accountIdPlaceholder: 'Your Upstox User ID',
    accountIdHelperText: 'Your Upstox User ID',
    accountIdMinLength: DEFAULT_ACCOUNT_ID_MIN_LENGTH,
    step2NotRequiredCopy: 'No API keys required for Upstox.',
    apiKeyLabel: 'Upstox API Key',
    apiSecretLabel: 'Upstox API Secret',
    apiKeyHelperText: 'Add from your Upstox app settings or developer console',
    apiSecretHelperText: 'API secret from the same app (keep it private)',
  },
  angelone: {
    authModes: ['oauth'],
    requiresApiCredentials: false,
    accountIdLabel: 'Client ID',
    accountIdPlaceholder: 'Your Angel One client ID',
    accountIdHelperText: 'Your Angel One client identifier',
    accountIdMinLength: DEFAULT_ACCOUNT_ID_MIN_LENGTH,
    step2NotRequiredCopy: 'No API keys required for Angel One.',
  },
  fyers: {
    authModes: ['oauth'],
    requiresApiCredentials: true,
    accountIdLabel: 'Fyers Client ID',
    accountIdPlaceholder: 'Enter your Fyers Client ID eg ABC123456',
    accountIdHelperText: 'Your Fyers Client ID eg ABC123456',
    accountIdMinLength: 2,
    step2NotRequiredCopy: 'No API keys required for Fyers.',
    apiKeyLabel: 'App ID',
    apiSecretLabel: 'Secret Key',
    apiKeyHelperText: 'Create an app at myapi.fyers.in and paste the App ID',
    apiSecretHelperText: 'Secret Key from the same app (keep it private)',
  },
  groww: {
    authModes: ['totp'],
    requiresApiCredentials: false,
    accountIdLabel: 'Groww Unique Client Code',
    accountIdPlaceholder: 'e.g., GRW123456',
    accountIdHelperText: 'Your Groww Unique Client Code',
    accountIdMinLength: DEFAULT_ACCOUNT_ID_MIN_LENGTH,
    step2NotRequiredCopy: 'No API keys required for Groww.',
  },
  dhan: {
    authModes: ['totp'],
    requiresApiCredentials: false,
    accountIdLabel: 'Dhan Client ID',
    accountIdPlaceholder: 'e.g., 1000000401',
    accountIdHelperText: 'Your Dhan Client ID',
    accountIdMinLength: DEFAULT_ACCOUNT_ID_MIN_LENGTH,
    step2NotRequiredCopy: 'No API keys required for Dhan.',
  },
};

export function getBrokerConfig(brokerType: string): BrokerRegistrationConfig | null {
  const key = brokerType.toLowerCase().trim();
  return BROKER_REGISTRATION_CONFIG[key] ?? null;
}

export function getSupportedBrokerTypes(): string[] {
  return Object.keys(BROKER_REGISTRATION_CONFIG);
}

export interface ReferenceStepInstruction {
  text: string;
  imagePath?: string;
  copyText?: string;
  links?: { url: string; text: string }[];
}

/** Reference panel content per broker and step (1-indexed). */
export interface ReferenceStepContent {
  title: string;
  body?: string;
  links?: { url: string; text: string }[];
  imagePath?: string;
  instructions?: ReferenceStepInstruction[];
}

const REFERENCE_CONTENT: Record<string, Record<number, ReferenceStepContent>> = {
  zerodha: {
    1: {
      title: 'Where to find Client ID',
      body: 'Your Zerodha Client ID is shown in the Kite app or on the Zerodha website after you log in. It is usually a short alphanumeric code (e.g. AB1234).',
      links: [{ url: 'https://kite.zerodha.com', text: 'Kite Web' }],
      imagePath: '/assets/images/brokers/zerodha/zerodha-1.png',
    },
    2: {
      title: 'Where to find API Key & Secret',
      instructions: [
        {
          text: 'Create an app in Kite Connect',
          links: [{ url: 'https://kite.zerodha.com/apps', text: 'Kite Connect – Your Apps' }],
          imagePath: '/assets/images/brokers/zerodha/zerodha-2a.png',
        },
        {
          text: 'Copy and paste the redirect URL shown below into the app settings',
          imagePath: '/assets/images/brokers/zerodha/zerodha-2b.png',
          copyText: 'https://api.flowrithm.com/api/v1/brokers/oauth/callback/zerodha',
        },
        {
          text: 'Get your API Key and API Secret from the app details',
          imagePath: '/assets/images/brokers/zerodha/zerodha-2c.png',
        }
      ],
    },
    3: {
      title: 'Almost done',
      body: 'Review your details. For OAuth you will be redirected to Zerodha to sign in. For TOTP, your credentials are stored securely and used for automated reconnection.',
    },
  },
  upstox: {
    1: {
      title: 'Where to find User ID',
      body: 'Your Upstox User ID is available in your Upstox app or web dashboard under account/profile settings.',
      links: [{ url: 'https://upstox.com', text: 'Upstox' }],
      imagePath: '/assets/images/brokers/upstox/upstox-step1.png',
    },
    2: {
      title: 'Where to find API Key & Secret',
      instructions: [
        {
          text: 'Create an app in the Upstox Developer Console',
          links: [{ url: 'https://account.upstox.com/developer/apps', text: 'Upstox Developer Console' }],
          imagePath: '/assets/images/brokers/upstox/upstox-step2a.png',
        },
        {
          text: 'Copy and paste the redirect URL shown below into the app settings',
          imagePath: '/assets/images/brokers/upstox/upstox-step2b.png',
          copyText: 'https://api.flowrithm.com/api/v1/brokers/oauth/callback/upstox',
        },
        {
          text: 'Get your API Key and API Secret from the app details',
          imagePath: '/assets/images/brokers/upstox/upstox-step2c.png',
        }
      ],
    },
    3: {
      title: 'Almost done',
      body: 'Review your details. You will be redirected to Upstox to authorise the connection.',
    },
  },
  angelone: {
    1: {
      title: 'Where to find Client ID',
      body: 'Your Angel One Client ID is available in the Angel One app or website after login, usually under profile or account details.',
    },
    2: {
      title: 'No API keys needed',
      body: 'Angel One does not require API Key/Secret for linking your account via OAuth.',
    },
    3: {
      title: 'Almost done',
      body: 'Review your details. You will be redirected to Angel One to sign in and authorise.',
    },
  },
  fyers: {
    1: {
      title: 'Where to find Fyers User ID',
      body: 'Your Fyers User ID is shown in the Fyers app or web platform after you log in. Enter it exactly as shown.',
      links: [{ url: 'https://fyers.in', text: 'Fyers' }],
      imagePath: '/assets/images/brokers/fyers/fyers-step1.png',
    },
    2: {
      title: 'Where to find App ID & Secret',
      instructions: [
        {
          text: 'Go to Profile --> APIs in the Fyers dashboard',
          links: [{ url: 'https://myapi.fyers.in/dashboard', text: 'Fyers API Dashboard' }],
          imagePath: '/assets/images/brokers/fyers/fyers-step2a.png',
        },
        {
          text: 'Create a new App',
          imagePath: '/assets/images/brokers/fyers/fyers-step2b.png',
        },
        {
          text: 'Copy and paste the redirect URL shown below',
          imagePath: '/assets/images/brokers/fyers/fyers-step2c.png',
          copyText: 'https://api.flowrithm.com/api/v1/brokers/oauth/callback/fyers',
        },
        {
          text: 'Note your App ID and Secret Key from the app details',
          imagePath: '/assets/images/brokers/fyers/fyers-step2d.png',
        }
      ],
    },
    3: {
      title: 'Almost done',
      body: 'Review your details. You will be redirected to Fyers to sign in and authorise.',
    },
  },
  groww: {
    1: {
      title: 'Where to find Unique Client Code',
      body: 'Your Unique Client Code [UCC] is available from Profile -> Settings -> Trading Details of your Groww account.',
      imagePath: '/assets/images/brokers/groww/groww-step1.png',
    },
    2: {
      title: 'Where to find TOTP Token & Secret',
      instructions: [
        {
          text: 'Navigate to Profile -> Settings -> Trading APIs',
          links: [{ url: 'https://groww.in/trade-api/api-keys', text: 'Groww Trading APIs' }],
          imagePath: '/assets/images/brokers/groww/groww-step2a.png',
        },
        {
          text: 'Click on drop down button on "Generate API Key"',
          imagePath: '/assets/images/brokers/groww/groww-step2b.png',
        },
        {
          text: 'Provide any suitable name and click Continue',
          imagePath: '/assets/images/brokers/groww/groww-step2c.png',
        },
        {
          text: 'Note your TOTP Token and TOTP Secret Code',
          imagePath: '/assets/images/brokers/groww/groww-step2d.png',
        }
      ],
    },
    3: {
      title: 'Almost done',
      body: 'For TOTP, enter your TOTP token and secret. Credentials are stored securely for automated reconnection.',
    },
  },
  dhan: {
    1: {
      title: 'Where to find Dhan Client ID',
      body: 'Dhan Client ID from Profile of your Dhan account',
      links: [{ url: 'https://web.dhan.co', text: 'Dhan Web' }],
      imagePath: '/assets/images/brokers/dhan/dhan-step1.png',
    },
    2: {
      title: 'Where to find TOTP Secret',
      instructions: [
        {
          text: 'Navigate to Profile -> DhanHQ Trading APIs. Setup TOTP',
          links: [{ url: 'https://web.dhan.co', text: 'Dhan Web – Setup TOTP' }],
          imagePath: '/assets/images/brokers/dhan/dhan-step2a.png',
        },
        {
          text: 'Next follow on-screen instructions on Dhan portal and note down TOTP',
        }
      ],
    },
    3: {
      title: 'Almost done',
      body: 'Review and submit. Your Dhan Pin and TOTP secret are stored securely for automated reconnection.',
    },
  },
};

const SECURITY_NOTE =
  'Credentials are stored securely and never shown again. We use industry-standard encryption and do not store your broker login password for OAuth flow.';

export function getReferenceContent(brokerType: string, stepIndex: number): ReferenceStepContent & { securityNote: string } {
  const key = brokerType.toLowerCase().trim();
  const stepContent = REFERENCE_CONTENT[key]?.[stepIndex] ?? {
    title: 'Need help?',
    body: 'Check your broker’s website or app for account and API details.',
  };
  return { ...stepContent, securityNote: SECURITY_NOTE };
}
